import { z } from 'zod';
import { LocalStorageGenerator, localStorageGet, localStorageSet } from './utils';
import { getCurrentMission, getCurrentSession } from './dataModelHelpers';
import { dispatchUpdatedCurrentSample } from './sessionEvents';
import { alertFullScreen, alertWarn } from './alertDispatcher';
import logger from './logger';
import { Sample } from './db';
import { BarcodeSelectionMode, MissionRobotEvents } from './types/types';
import { SAMPLING_TASK } from './constants';
import { BarcodeProcessorStateStore } from './barcodes';
import EventBus from './EventBus';
import { SCAN_EVENTS, ScannedBarcodeEvent } from './scanEvents';
import { errorTone } from './alertTones';

export const SampleSelectionStates = ['CORING', 'PULLED', 'DUMPED'] as const;
export type SampleSelectionState = (typeof SampleSelectionStates)[number];

interface SampleSelection {
  sampleId: string;
  state: SampleSelectionState;
}

interface SampleSelectionStateMachine {
  jobId: string;
  currentSampleState?: SampleSelection;
  nextSampleState?: SampleSelection;
}

export const SampleSelectionStateMachineStore = LocalStorageGenerator<SampleSelectionStateMachine>(
  'sampleSelectionStateMachine',
  {
    jobId: '',
    currentSampleState: undefined,
    nextSampleState: undefined,
  },
);

export function sampleSelectionAdvance(isNextSampleState = true) {
  const stateKey = isNextSampleState ? 'nextSampleState' : 'currentSampleState';
  const currentState = SampleSelectionStateMachineStore.get();
  const stateToAdvance = currentState[stateKey]?.state;

  if (!stateToAdvance) {
    return;
  }

  const index = SampleSelectionStates.indexOf(stateToAdvance);

  if (index === -1) {
    return;
  }

  const nextIndex = (index + 1) % SampleSelectionStates.length;
  const newState = SampleSelectionStates[nextIndex];

  SampleSelectionStateMachineStore.set({
    ...currentState,
    [stateKey]: {
      ...currentState[stateKey],
      state: newState,
    },
  });
}

const LastSampleSelectionKey = 'lastSampleSelection';

// make zod object for LastSampleSelected
export const LastSampleSelected = z.object({
  sampleInstanceId: z.optional(z.number()),
  timestamp: z.date(),
});

export type LastSampleSelected = z.infer<typeof LastSampleSelected>;

export const SampleToNavigateTo = z.object({
  sampleOrCoreId: z.string(),
  lat: z.number(),
  lon: z.number(),
});

export type SampleToNavigateTo = z.infer<typeof SampleToNavigateTo>;

// export const SampleSelectionLogic = z.object({
//     currentSampleInstanceId: z.string().optional(),
//     currentSampleState: z.enum(SampleSelectionStates).optional(),
//     nextSampleInstanceId: z.string().optional(),
//     nextSampleState: z.enum(SampleSelectionStates).optional(),
// });
// // eslint-disable-next-line @typescript-eslint/no-redeclare
// export type SampleSelectionLogic = z.infer<typeof SampleSelectionLogic>;

export const BarcodeSelectionModeStore = LocalStorageGenerator<BarcodeSelectionMode>(
  'barcodeSelectionMode',
  'Automatic',
);

/**
 * Find a sample instance ID given a sample ID
 * @param sampleOrZoneOrCoreId
 * @returns Sample the sample we want
 */
export function findSampleById(sampleOrZoneOrCoreId?: string) {
  if (!sampleOrZoneOrCoreId) {
    return;
  }

  const mission = getCurrentMission();

  if (!mission) {
    return;
  }

  // if zone mission, then find the sample by core_id
  if (mission.is_zone_mission()) {
    const cores = mission.getSampleSites().getSamples().getSoilCores();
    const core = cores.find((core) => `${core.sample_id}.${core.core_number}` === sampleOrZoneOrCoreId);

    if (core) {
      return mission.getSamples().find((sample) => sample.sample_id === core.sample_id);
      //return await Sample.findOne((sample) => sample.sample_id === core.sample_id);
    } else {
      // could be a zone identifier
      const sampleZones = mission
        .getZones()
        .getSampleZones()
        .filter((zone) => zone.sample_id === sampleOrZoneOrCoreId);

      if (sampleZones.length === 1) {
        return sampleZones[0]?.getSampleSite()?.getSamples()[0];
      }

      return undefined;
    }
  }

  // if not zone mission, then find the sample by sample_id
  const samples = mission.getAllSamples();

  return samples.find((sample) => sample.sample_id === sampleOrZoneOrCoreId);
}

export const getSampleSelected = () => {
  const result = localStorageGet<LastSampleSelected | undefined>(LastSampleSelectionKey, undefined);
  console.log(`sampleSelectionHelpers.ts getSampleSelected() = ${JSON.stringify(result)}`);

  return result;
};

export const setSampleSelected = async (value?: LastSampleSelected) => {
  console.log(`sampleSelectionHelpers.ts setSampleSelected(${JSON.stringify(value || '')})`);
  const session = getCurrentSession();

  if (!session) {
    return;
  }

  const nextSample = Sample.get(value?.sampleInstanceId);

  // get last scanned sample from mission
  const mission = session.getMission();

  if (!mission) {
    return;
  }

  // order from newest to oldest
  const sortedSamples = mission
    .getAllSamples()
    .filter((sample) => sample.bag_id)
    .sort((sampleA, sampleB) => sampleA.pulled_at - sampleB.pulled_at);
  const lastScanned = sortedSamples.length ? sortedSamples[0] : undefined;

  const validValueReceived = !!value;
  const sessionHasCurrentSample = Boolean(session.Sample_id);
  const sampleIdChanged = session.Sample_id !== value?.sampleInstanceId;

  if (validValueReceived && sessionHasCurrentSample && sampleIdChanged) {
    const currentSample = session.getSample();

    // if the currently selected sample does NOT have a bag_id, then alert the user
    if (currentSample && !currentSample?.bag_id) {
      if (nextSample) {
        if (mission) {
          if (BarcodeSelectionModeStore.get() === 'Automatic') {
            BarcodeProcessorStateStore.set({ barcodeScanningPaused: true });
            //const SCAN_SAMPLE = `SCAN LAST SAMPLE (Sample ${currentSample.sample_id}) NOW`;
            const SCAN_SAMPLE = 'SCAN';
            //const SKIP_SAMPLE: [string, string] = [`I SKIPPED SAMPLE ${currentSample.sample_id}`, `ARE YOU SURE?`];
            const SKIP_SAMPLE: [string, string] = [`CANCEL`, `ARE YOU SURE?`];
            await errorTone();
            const result = await alertFullScreen(
              `2 UNSCANNED SAMPLES`,
              `Stop Robot and Decide!\n` +
                `In Bucket/Just Dumped: <b>Sample ${nextSample.sample_id}</b>\n` +
                `Previous Sample: <b>Sample ${currentSample.sample_id}</b>\n` +
                (lastScanned ? `Last Scanned: <b> Sample ${lastScanned.sample_id} (${lastScanned.bag_id})</b>\n` : '') +
                `How do you want to handle <b>Sample ${currentSample.sample_id}</b>?`,
              [SCAN_SAMPLE, SKIP_SAMPLE],
              '#FF0000',
              'Explanation (Call ops if unclear)\n' +
                '<b>SCAN</b> - You SAMPLED but have NOT scanned yet (look around cab & box)\n' +
                '<b>CANCEL</b> - you did NOT SAMPLE or have NO BAG (you will sample later)',
            );

            if (result === SCAN_SAMPLE) {
              BarcodeProcessorStateStore.set({ barcodeScanningPaused: false });
              const alertScan = alertFullScreen(
                `Scan Sample ${currentSample.sample_id} now`,
                'Or skip by pressing the button',
                [['SKIP this sample', 'Are you sure?']],
              );

              const barcodeScannedPromise = new Promise<boolean>((resolve) => {
                const barcodeScanned = (scanEvent: ScannedBarcodeEvent) => {
                  EventBus.remove(SCAN_EVENTS.SCANNED_BARCODE, barcodeScanned);

                  // request full screen popup to close
                  EventBus.dispatch(SAMPLING_TASK.CLOSE_SAMPLE_SELECTION_POPUP);
                  resolve(true);
                };

                EventBus.on(SCAN_EVENTS.SCANNED_BARCODE, barcodeScanned);
              });

              await Promise.any([alertScan, barcodeScannedPromise]);
            }

            BarcodeProcessorStateStore.set({ barcodeScanningPaused: false });
            await logger.log('SAMPLE_SELECTION', `Missed Sample? ${result}`);
          }
        }
      }
    }
  }

  // if the NEXT sample already has a barcode, then we will warn the user
  if (nextSample?.bag_id) {
    alertWarn('WARNING: You are selecting a sample that ALREADY has a scanned barcode');
  }

  // TODO will leave this for now if we want to add the timeout feature
  // if (value?.sampleInstanceId && !DisableBarcodeEnforcement.get()) {
  //     const sampleSelectionTimeout = setTimeout(async () => {
  //         const session = getCurrentSession();
  //         if (!session) return;
  //         const currentSample = session.getSample();

  //         // we should have correctly captured the `value` state such that if for some reason
  //         // the sample is undefined or the sampleInstanceId is not the same as the currentSample
  //         // then we should not alert the user
  //         if (currentSample && (currentSample.instance_id === value.sampleInstanceId)) {
  //             await setSampleSelected(undefined);
  //             alertWarn(`Sample ${currentSample.sample_id} was not scanned in time and was unselected (1)`);
  //         }
  //         clearTimeout(sampleSelectionTimeout);
  //     }, SAMPLE_SELECTION_TIMEOUT_MS);
  // }

  const oldSampleInstanceId = session.Sample_id;
  session.Sample_id = value?.sampleInstanceId;
  localStorageSet(LastSampleSelectionKey, value);
  dispatchUpdatedCurrentSample([oldSampleInstanceId, value?.sampleInstanceId]);
};

export const DisableBarcodeEnforcement = LocalStorageGenerator<boolean>('disableBarcodeEnforcement', false);

/**
 * The mission-id memo. Only the missionId field is read anywhere in the
 * code base — see SamplingTask.tsx where it is used to detect a mission
 * change. The legacy `events` array used to be appended to on every core
 * completion via `AddMissionEvent`, but every consumer of the events was
 * commented out years ago, so the writes were O(N²) main-thread
 * JSON.stringify churn for data nobody read. The events array and
 * `AddMissionEvent` have been removed.
 */
export const MissionEventStore = LocalStorageGenerator<MissionRobotEvents>('missionEventStore', {
  missionId: '',
});
